/**
 * Saves a Traffic link (PATCH, unchanged) and, when the link moves to, from or
 * within Tres Commas, refreshes the country's stats queries: their cache keys
 * hold the wiserIds, not the tracker campaign, so the old link's numbers would
 * otherwise stay on screen. Voluum ↔ Binom saves do exactly what they did.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import {
  usePatchSmswiserCampaignTraffic,
  type PatchSmswiserCampaignTrafficBody,
  type PatchTrafficProvider,
} from '@/hooks';
import { smswiserCampaignWorkspaceKeys } from '@/hooks/smswiser-campaign-workspace/keys';

import { touchesTresCommas } from './link-choices';

const STATS_QUERIES = [
  'dropsWithStats',
  'listsWithStats',
  'textsWithStats',
  'gatewaysWithStats',
  'sourcesWithStats',
  'phonesWithStats',
  'pathsWithStats',
  'landersWithStats',
  'offersWithStats',
] as const;

function refreshCountryStats(queryClient: QueryClient, countryKey: string): void {
  for (const name of STATS_QUERIES) {
    void queryClient.invalidateQueries({
      queryKey: [...smswiserCampaignWorkspaceKeys.all, name, countryKey],
    });
  }
}

export function useTrafficLinkSave(countryKey: string, initialProvider: PatchTrafficProvider) {
  const patch = usePatchSmswiserCampaignTraffic(countryKey);
  const queryClient = useQueryClient();
  const save = async (body: PatchSmswiserCampaignTrafficBody) => {
    await patch.mutateAsync(body);
    if (touchesTresCommas(initialProvider, body.trafficProvider)) {
      refreshCountryStats(queryClient, countryKey);
    }
  };
  return { save, isPending: patch.isPending, error: patch.error };
}
